import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from 'src/store';
import { getRewardsBalance } from 'src/models/Rewards';
import type { RewardsBalance } from 'src/models/Rewards';

interface RewardsState {
  companyId: string | null;
  balance: number | null;
  earnedTotal: number;
  redeemedTotal: number;
  loading: boolean;
  error: string | null;
  activeRequestId: number;
}

let nextRewardsRequestId = 1;

const initialState: RewardsState = {
  companyId: null,
  balance: null,
  earnedTotal: 0,
  redeemedTotal: 0,
  loading: false,
  error: null,
  activeRequestId: 0,
};

const slice = createSlice({
  name: 'rewards',
  initialState,
  reducers: {
    setActiveCompany(state: RewardsState, action: PayloadAction<string>) {
      if (state.companyId === action.payload) {
        return;
      }

      state.companyId = action.payload;
      state.balance = null;
      state.earnedTotal = 0;
      state.redeemedTotal = 0;
      state.loading = true;
      state.error = null;
    },
    startRequest(
      state: RewardsState,
      action: PayloadAction<{
        companyId: string;
        requestId: number;
        showLoading: boolean;
      }>
    ) {
      state.companyId = action.payload.companyId;
      state.activeRequestId = action.payload.requestId;
      state.loading = action.payload.showLoading;
      state.error = null;
    },
    resolveRewardsBalance(
      state: RewardsState,
      action: PayloadAction<{
        companyId: string;
        requestId: number;
        data: RewardsBalance;
      }>
    ) {
      if (
        action.payload.companyId !== state.companyId ||
        action.payload.requestId !== state.activeRequestId
      ) {
        return;
      }

      const { balance, earnedTotal, redeemedTotal } = action.payload.data;
      state.balance = balance;
      state.earnedTotal = earnedTotal;
      state.redeemedTotal = redeemedTotal;
      state.loading = false;
      state.error = null;
    },
    setRealtimeBalance(
      state: RewardsState,
      action: PayloadAction<{ companyId: string; balance: number }>
    ) {
      if (action.payload.companyId !== state.companyId) {
        return;
      }

      state.balance = action.payload.balance;
      state.loading = false;
      state.error = null;
    },
    rejectRequest(
      state: RewardsState,
      action: PayloadAction<{
        companyId: string;
        requestId: number;
        error: string;
      }>
    ) {
      if (
        action.payload.companyId !== state.companyId ||
        action.payload.requestId !== state.activeRequestId
      ) {
        return;
      }

      state.error = action.payload.error;
      state.loading = false;
    },
    clearRewards(state: RewardsState) {
      state.companyId = null;
      state.balance = null;
      state.earnedTotal = 0;
      state.redeemedTotal = 0;
      state.loading = false;
      state.error = null;
      state.activeRequestId = 0;
    },
  },
});

export const reducer = slice.reducer;

export const { setActiveCompany, setRealtimeBalance, clearRewards } =
  slice.actions;

/**
 * Fetch rewards balance and update Redux store
 */
export const fetchRewardsBalance =
  (companyId: string): AppThunk =>
  async (dispatch) => {
    const requestId = nextRewardsRequestId++;

    try {
      dispatch(
        slice.actions.startRequest({ companyId, requestId, showLoading: true })
      );
      const data = await getRewardsBalance(companyId);
      dispatch(
        slice.actions.resolveRewardsBalance({ companyId, requestId, data })
      );
    } catch (error: any) {
      dispatch(
        slice.actions.rejectRequest({
          companyId,
          requestId,
          error: error?.message || 'Failed to fetch rewards balance',
        })
      );
    }
  };

/**
 * Refresh rewards balance after earning or redeeming points
 */
export const refreshRewardsBalance =
  (companyId: string): AppThunk =>
  async (dispatch, getState) => {
    const currentCompanyId = getState().rewards.companyId;
    const requestId = nextRewardsRequestId++;

    try {
      if (currentCompanyId !== companyId) {
        dispatch(slice.actions.setActiveCompany(companyId));
      }

      dispatch(
        slice.actions.startRequest({
          companyId,
          requestId,
          showLoading: false,
        })
      );

      const data = await getRewardsBalance(companyId);
      dispatch(
        slice.actions.resolveRewardsBalance({ companyId, requestId, data })
      );
    } catch (error: any) {
      dispatch(
        slice.actions.rejectRequest({
          companyId,
          requestId,
          error: error?.message || 'Failed to refresh rewards balance',
        })
      );

      // Keep noisy logs only for unexpected refresh failures.
      console.error('Failed to refresh rewards balance:', error);
    }
  };

export default slice;
